import BaseEllipsisText from '@/components/base/BaseEllipsisText';
import useMenu, { type MenuUINode } from '@/hooks/useMenu';
import { useIntl } from '@/i18n';
import { history } from '@/router/navigate';
import { useAppStore } from '@/store/app';
import { isExternal } from '@/utils';
import { feedback } from '@/utils/feedback';
import { renderIcon } from '@/utils/icon';
import { moduleGateReasonText } from '@/utils/moduleGate';
import { LockOutlined, RightOutlined } from '@ant-design/icons';
import { Menu, type MenuProps } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

const groupIcons: Record<string, string> = {
  'acct::mgt': 'TeamOutlined',
  'member::person': 'TeamOutlined',
  'member::mgt': 'BookOutlined',
  'member::qual': 'BookOutlined',
  'member::cert': 'CalendarOutlined',
  'member::club': 'BookOutlined',
  'org::affairs': 'BookOutlined',
  'org::mgt': 'BookOutlined',
  'org::secretariat': 'BookOutlined',
  'ops::site': 'BookOutlined',
  'ops::msg': 'BookOutlined',
  'ops::other': 'BookOutlined',
  'order::mgt': 'TeamOutlined',
  'fin::mgt': 'WalletOutlined',
  'sys::biz': 'BookOutlined',
  'sys::system': 'ControlOutlined',
  'sys::log': 'BookOutlined',
};

type MenuItems = Required<MenuProps>['items'];

interface SideMenuProps {
  moduleCode?: string;
  mobile?: boolean;
  onNavigate?: () => void;
  /** 菜单键 → 待办数；由布局层统一取一次传下来，避免一级栏与二级栏各发一次请求 */
  badges: Record<string, number>;
}

/** 只取渲染时要用到的三格，避免把 antd 的联合类型摊得到处都是 */
interface BadgeableItem {
  key?: string;
  icon?: ReactNode;
  label?: ReactNode;
  children?: BadgeableItem[];
  type?: string;
}

function badgeNode(count: number): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      className="admin-menu-badge box-border absolute top-[50%] end-0 inline-flex min-w-4.5 h-4.5 items-center justify-center py-0 px-[5px] rounded-full bg-badge-bg text-badge-text text-[10px] leading-[1] pointer-events-none -translate-y-1/2"
      aria-hidden
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function withBadge(label: ReactNode, count: number, gated: boolean): ReactNode {
  const node = badgeNode(count);

  return (
    // antd 直接使用 span 标签作为标题，保留标题类才能清除默认图文间距并正确伸缩。
    <span className="ant-menu-title-content">
      <span className="admin-menu-label box-border relative flex items-center w-full min-w-0 pe-8.5">
        {/* 标题独占剩余宽度并截断：英文菜单名很长，不截断会把右边的数字挤出去 */}
        <span className="admin-menu-title min-w-0 flex-1">
          {typeof label === 'string' ? (
            <BaseEllipsisText title={label} />
          ) : (
            label
          )}
        </span>
        {gated && <LockOutlined className="admin-menu-lock" />}
        {node}
      </span>
    </span>
  );
}

/** 分组行上的数是它子树之和：展开与收起均显示（PRD 27750–27755） */
function subtreeSum(
  item: BadgeableItem,
  badges: Record<string, number>,
): number {
  if (item.children?.length) {
    return item.children.reduce(
      (total, child) => total + subtreeSum(child, badges),
      0,
    );
  }
  return item.key ? badges[item.key] ?? 0 : 0;
}

/**
 * 把菜单项换成带数字的 label：页面项挂自己的数，分组行始终显示子树之和。
 * 0 不渲染——这与一级栏同一条口径（PRD 27703–27712）。
 */
function applyBadges(
  items: MenuItems,
  badges: Record<string, number>,
  openKeys: string[],
  menuMap: Record<string, MenuUINode>,
): MenuItems {
  return (items ?? []).map((raw) => {
    const item = raw as unknown as BadgeableItem;
    const hasChildren = Boolean(item.children?.length);
    const children = hasChildren
      ? applyBadges(item.children as MenuItems, badges, openKeys, menuMap)
      : undefined;
    const count = hasChildren
      ? subtreeSum(item, badges)
      : item.key
      ? badges[item.key] ?? 0
      : 0;
    // 锁标记按页面自己的 gated 取（useMenu 已按所属模块判过），不在这里再判模块
    const gated = Boolean(item.key && menuMap[item.key]?.gated);
    const { type: _type, ...menuItem } = raw as unknown as BadgeableItem;
    return {
      ...menuItem,
      icon: hasChildren
        ? item.key && groupIcons[item.key]
          ? renderIcon(groupIcons[item.key])
          : item.icon
        : undefined,
      label: withBadge(item.label, count, gated),
      ...(children ? { children } : {}),
    } as MenuItems[number];
  });
}

/** 侧边栏菜单：数据来自后端权限树 */
export default function SideMenu({
  moduleCode,
  badges,
  mobile = false,
  onNavigate,
}: SideMenuProps) {
  const { items, moduleItems, menuMap, selectedKeys, openKeys } = useMenu();
  // 只订阅用到的字段：改主题色等其他设置不会让菜单重渲染
  const dark = useAppStore((state) => state.dark);
  const intl = useIntl();
  const [stateOpenKeys, setStateOpenKeys] = useState<string[]>([
    ...openKeys,
    ...Object.values(menuMap)
      .filter((node) => !mobile && node.kind === 'group')
      .map((node) => node.code),
  ]);

  const visibleItems = moduleCode ? moduleItems[moduleCode] ?? [] : items;
  const visibleOpenKeys = moduleCode
    ? openKeys.filter((key) => key !== moduleCode)
    : openKeys;

  const badgeItems = useMemo(
    () => applyBadges(visibleItems, badges, stateOpenKeys, menuMap),
    [visibleItems, badges, stateOpenKeys, menuMap],
  );

  useEffect(() => {
    setStateOpenKeys((prev) =>
      Array.from(new Set([...prev, ...visibleOpenKeys])),
    );
  }, [visibleOpenKeys.join(',')]);

  const handleClick = ({ key }: { key: string }) => {
    const node = menuMap[key];
    if (!node) return;
    // 锁着只说明原因、不跳转；不用 antd 的 disabled——点了完全没反馈（PRD 27740–27743）
    if (node.gated) {
      feedback.message.warning(moduleGateReasonText(intl, node.reason ?? null));
      return;
    }
    if (!node.path) return;

    // 外链：让用户选择新窗口打开还是站内内嵌打开
    if (isExternal(node.path)) {
      feedback.modal.confirm({
        title: intl.formatMessage({ id: 'layout.openTypeTitle' }),
        content: node.path,
        okText: intl.formatMessage({ id: 'layout.openInNewWindow' }),
        cancelText: intl.formatMessage({ id: 'layout.openInIframe' }),
        closable: false,
        mask: { closable: false },
        onOk: () => window.open(node.path, '_blank'),
        onCancel: () =>
          history.push(
            `/iframe?code=${node.code}&url=${encodeURIComponent(
              node.path as string,
            )}`,
          ),
      });
      return;
    }
    history.push(node.path);
    onNavigate?.();
  };

  return (
    <Menu
      mode="inline"
      theme={dark ? 'dark' : 'light'}
      inlineIndent={18}
      items={badgeItems}
      selectedKeys={selectedKeys}
      openKeys={stateOpenKeys}
      onOpenChange={(keys) => setStateOpenKeys(keys as string[])}
      inlineCollapsed={false}
      expandIcon={({ isOpen }) => (
        <RightOutlined
          rotate={isOpen ? 90 : 0}
          className="admin-menu-expand-icon"
        />
      )}
      onClick={handleClick}
      className="border-e-0"
    />
  );
}
